import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from "@angular/core";
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { SHOW_TALENT_POOL_UI } from "@ankaadia/ankaadia-shared";
import { translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import { ActivityType } from "@ankaadia/graphql";
import { noop } from "lodash";
import { MultiSelect } from "primeng/multiselect";
import { EnumPipe } from "../../../shared/pipes/enum.pipe";
import { NewUiMultiSelectSortOnTopDirective } from "../../../shared/primeng/multi-select-sort-selected-values-on-top/new-ui-multi-select-sort-on-top.directive";
import { TranslateDirective } from "../../../shared/transloco/translate.directive";
import { NewUiActivityIconComponent } from "../activity-icon/new-ui-activity-icon.component";

@Component({
  selector: "new-ui-activity-type-selector",
  templateUrl: "./new-ui-activity-type-selector.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: NewUiActivityTypeSelectorComponent, multi: true }],
  imports: [
    TranslateDirective,
    MultiSelect,
    NewUiMultiSelectSortOnTopDirective,
    FormsModule,
    NewUiActivityIconComponent,
    EnumPipe,
  ],
})
export class NewUiActivityTypeSelectorComponent implements ControlValueAccessor {
  readonly types = [
    { label: translateSafely("enum.ActivityType.CandidateAdded"), value: ActivityType.CandidateAdded },
    { label: translateSafely("enum.ActivityType.CandidateUpdated"), value: ActivityType.CandidateUpdated },
    { label: translateSafely("enum.ActivityType.CandidateDeleted"), value: ActivityType.CandidateDeleted },
    {
      label: translateSafely("enum.ActivityType.CandidateDeletionReminder"),
      value: ActivityType.CandidateDeletionReminder,
    },
    {
      label: translateSafely("enum.ActivityType.CandidateRequestDeletion"),
      value: ActivityType.CandidateRequestDeletion,
    },
    { label: translateSafely("enum.ActivityType.CandidateBirthday"), value: ActivityType.CandidateBirthday },
    { label: translateSafely("enum.ActivityType.CandidateBirthdaySoon"), value: ActivityType.CandidateBirthdaySoon },
    { label: translateSafely("enum.ActivityType.FileUploaded"), value: ActivityType.FileUploaded },
    { label: translateSafely("enum.ActivityType.FileModified"), value: ActivityType.FileModified },
    { label: translateSafely("enum.ActivityType.FileDeleted"), value: ActivityType.FileDeleted },
    { label: translateSafely("enum.ActivityType.FileExpiresSoon"), value: ActivityType.FileExpiresSoon },
    { label: translateSafely("enum.ActivityType.TranslationUploaded"), value: ActivityType.TranslationUploaded },
    { label: translateSafely("enum.ActivityType.FreeFormatFileUploaded"), value: ActivityType.FreeFormatFileUploaded },
    { label: translateSafely("enum.ActivityType.FreeFormatFileDeleted"), value: ActivityType.FreeFormatFileDeleted },
    {
      label: translateSafely("enum.ActivityType.DocumentSetMetaDataModified"),
      value: ActivityType.DocumentSetMetaDataModified,
    },

    { label: translateSafely("enum.ActivityType.CollectionAdded"), value: ActivityType.CollectionAdded },
    { label: translateSafely("enum.ActivityType.CollectionDeleted"), value: ActivityType.CollectionDeleted },
    { label: translateSafely("enum.ActivityType.CollectionUpdated"), value: ActivityType.CollectionUpdated },
    {
      label: translateSafely("enum.ActivityType.CandidateAddedToCollection"),
      value: ActivityType.CandidateAddedToCollection,
    },
    {
      label: translateSafely("enum.ActivityType.CandidateRemovedFromCollection"),
      value: ActivityType.CandidateRemovedFromCollection,
    },
    {
      label: translateSafely("enum.ActivityType.CollectionEmbeddedIntoCollection"),
      value: ActivityType.CollectionEmbeddedIntoCollection,
    },
    {
      label: translateSafely("enum.ActivityType.CollectionRemovedFromCollection"),
      value: ActivityType.CollectionRemovedFromCollection,
    },
    { label: translateSafely("enum.ActivityType.SharingAdded"), value: ActivityType.SharingAdded },
    { label: translateSafely("enum.ActivityType.SharingDeleted"), value: ActivityType.SharingDeleted },
    { label: translateSafely("enum.ActivityType.SharingUpdated"), value: ActivityType.SharingUpdated },
    { label: translateSafely("enum.ActivityType.SharingEnabled"), value: ActivityType.SharingEnabled },
    { label: translateSafely("enum.ActivityType.SharingDisabled"), value: ActivityType.SharingDisabled },
    { label: translateSafely("enum.ActivityType.SharingTypeModified"), value: ActivityType.SharingTypeModified },
    { label: translateSafely("enum.ActivityType.ProcessCreated"), value: ActivityType.ProcessCreated },
    { label: translateSafely("enum.ActivityType.ProcessDeleted"), value: ActivityType.ProcessDeleted },
    { label: translateSafely("enum.ActivityType.TaskManuallyCreated"), value: ActivityType.TaskManuallyCreated },
    { label: translateSafely("enum.ActivityType.TaskDeleted"), value: ActivityType.TaskDeleted },
    { label: translateSafely("enum.ActivityType.TaskStarted"), value: ActivityType.TaskStarted },
    { label: translateSafely("enum.ActivityType.TaskFinished"), value: ActivityType.TaskFinished },
    { label: translateSafely("enum.ActivityType.TaskCancelled"), value: ActivityType.TaskCancelled },
    { label: translateSafely("enum.ActivityType.TaskStopped"), value: ActivityType.TaskStopped },
    { label: translateSafely("enum.ActivityType.TaskFailed"), value: ActivityType.TaskFailed },
    { label: translateSafely("enum.ActivityType.TaskChanged"), value: ActivityType.TaskChanged },
    { label: translateSafely("enum.ActivityType.TaskManuallyChanged"), value: ActivityType.TaskManuallyChanged },
    { label: translateSafely("enum.ActivityType.TaskReminderSent"), value: ActivityType.TaskReminderSent },
    { label: translateSafely("enum.ActivityType.TaskStateChanged"), value: ActivityType.TaskStateChanged },
    {
      label: translateSafely("enum.ActivityType.TaskStateManuallyChanged"),
      value: ActivityType.TaskStateManuallyChanged,
    },
    { label: translateSafely("enum.ActivityType.EmailNotDelivered"), value: ActivityType.EmailNotDelivered },
    { label: translateSafely("enum.ActivityType.CandidateLoggedIn"), value: ActivityType.CandidateLoggedIn },
    { label: translateSafely("enum.ActivityType.CandidateLoggedIn"), value: ActivityType.UserLoggedIn },
    { label: translateSafely("enum.ActivityType.FeedbackProvided"), value: ActivityType.FeedbackProvided },
    { label: translateSafely("enum.ActivityType.FeedbackCommentAdded"), value: ActivityType.FeedbackCommentAdded },
    { label: translateSafely("enum.ActivityType.CandidateDroppedOut"), value: ActivityType.CandidateDroppedOut },
    {
      label: translateSafely("enum.ActivityType.AutoProcessOperationTriggered"),
      value: ActivityType.AutoProcessOperationTriggered,
    },
    { label: translateSafely("enum.ActivityType.TaskNoteAdded"), value: ActivityType.TaskNoteAdded },
    {
      label: translateSafely("enum.ActivityType.ProcessModelReleaseCreated"),
      value: ActivityType.ProcessModelReleaseCreated,
    },
    { label: translateSafely("enum.ActivityType.ProcessModelPublished"), value: ActivityType.ProcessModelPublished },
    {
      label: translateSafely("enum.ActivityType.ProcessModelUnpublished"),
      value: ActivityType.ProcessModelUnpublished,
    },
    { label: translateSafely("enum.ActivityType.ProcessModelDeleted"), value: ActivityType.ProcessModelDeleted },
    { label: translateSafely("enum.ActivityType.CandidateOptedOut"), value: ActivityType.CandidateOptedOut },
    { label: translateSafely("enum.ActivityType.CandidateOptedIn"), value: ActivityType.CandidateOptedIn },
    { label: translateSafely("enum.ActivityType.CandidateNotFound"), value: ActivityType.CandidateNotFound },
    { label: translateSafely("enum.ActivityType.UserNotFound"), value: ActivityType.UserNotFound },
    { label: translateSafely("enum.ActivityType.CollectionNotFound"), value: ActivityType.CollectionNotFound },
    { label: translateSafely("enum.ActivityType.OrganizationNotFound"), value: ActivityType.OrganizationNotFound },
    { label: translateSafely("enum.ActivityType.ProcessNotFound"), value: ActivityType.ProcessNotFound },
    { label: translateSafely("enum.ActivityType.DocumentNotFound"), value: ActivityType.DocumentNotFound },
    { label: translateSafely("enum.ActivityType.TaskNotFound"), value: ActivityType.TaskNotFound },
    {
      label: translateSafely("enum.ActivityType.InvalidSharingTemplate"),
      value: ActivityType.InvalidSharingTemplate,
    },
    {
      label: translateSafely("enum.ActivityType.InvalidUserGroupRoleMapping"),
      value: ActivityType.InvalidUserGroupRoleMapping,
    },
    {
      label: translateSafely("enum.ActivityType.AutoTeamRoleNotSetOrOwner"),
      value: ActivityType.AutoTeamRoleNotSetOrOwner,
    },
    {
      label: translateSafely("enum.ActivityType.AutoTeamNoResponsibleUser"),
      value: ActivityType.AutoTeamNoResponsibleUser,
    },
    { label: translateSafely("enum.ActivityType.OrganizationNotLinked"), value: ActivityType.OrganizationNotLinked },
    { label: translateSafely("enum.ActivityType.ConsentFeedback"), value: ActivityType.ConsentFeedback },
    { label: translateSafely("enum.ActivityType.OnlineApplicationRun"), value: ActivityType.OnlineApplicationRun },
    {
      label: translateSafely("enum.ActivityType.AutoProcessOperationFailed"),
      value: ActivityType.AutoProcessOperationFailed,
    },
    { label: translateSafely("enum.ActivityType.BaseProcessNotFound"), value: ActivityType.BaseProcessNotFound },
    { label: translateSafely("enum.ActivityType.ProcessModelNotFound"), value: ActivityType.ProcessModelNotFound },
    { label: translateSafely("enum.ActivityType.EducationExamCreated"), value: ActivityType.EducationExamCreated },
    { label: translateSafely("enum.ActivityType.EducationExamUpdated"), value: ActivityType.EducationExamUpdated },
    { label: translateSafely("enum.ActivityType.EducationExamDeleted"), value: ActivityType.EducationExamDeleted },
    {
      label: translateSafely("enum.ActivityType.EducationExamResultCreated"),
      value: ActivityType.EducationExamResultCreated,
    },
    {
      label: translateSafely("enum.ActivityType.EducationExamResultDeleted"),
      value: ActivityType.EducationExamResultDeleted,
    },
    { label: translateSafely("enum.ActivityType.CandidateAddedToExam"), value: ActivityType.CandidateAddedToExam },
    {
      label: translateSafely("enum.ActivityType.CandidateRemovedFromExam"),
      value: ActivityType.CandidateRemovedFromExam,
    },
    { label: translateSafely("enum.ActivityType.ContractCreated"), value: ActivityType.ContractCreated },
    { label: translateSafely("enum.ActivityType.ContractAnnulled"), value: ActivityType.ContractAnnulled },
    { label: translateSafely("enum.ActivityType.ContractCancelled"), value: ActivityType.ContractCancelled },
    {
      label: translateSafely("enum.ActivityType.ContractCancellationWithdrawn"),
      value: ActivityType.ContractCancellationWithdrawn,
    },
    { label: translateSafely("enum.ActivityType.ContractPlanChanged"), value: ActivityType.ContractPlanChanged },
    { label: translateSafely("enum.ActivityType.InvoicePaid"), value: ActivityType.InvoicePaid },
    {
      label: translateSafely("enum.ActivityType.ChildOrganizationLinksSynced"),
      value: ActivityType.ChildOrganizationLinksSynced,
    },
    ...(SHOW_TALENT_POOL_UI
      ? [
          {
            label: translateSafely("enum.ActivityType.TalentPoolRequestReceived"),
            value: ActivityType.TalentPoolRequestReceived,
          },
          {
            label: translateSafely("enum.ActivityType.TalentPoolRequestApproved"),
            value: ActivityType.TalentPoolRequestApproved,
          },
          {
            label: translateSafely("enum.ActivityType.TalentPoolRequestRejected"),
            value: ActivityType.TalentPoolRequestRejected,
          },
        ]
      : []),
  ];

  @Input()
  inputId: string;

  @Input()
  formControl: FormControl;

  model: string[];
  disabled: boolean;
  onModelChange = noop;
  onModelTouched = noop;

  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  writeValue(model: string[]): void {
    this.model = model;
    this.changeDetector.markForCheck();
  }

  registerOnChange(onModelChange: () => void): void {
    this.onModelChange = onModelChange;
  }

  registerOnTouched(onModelTouched: () => void): void {
    this.onModelTouched = onModelTouched;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.changeDetector.markForCheck();
  }

  updateModel(model: string[]): void {
    this.model = model;
    this.onModelChange(model);
    this.formControl?.setValue(model);
  }
}
